import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, FileSpreadsheet, Trash2, Check, X, Ban, Loader2, ArrowRightLeft } from "lucide-react";
import { fmt, normalizePersonKey, expensePersonName, sortSources } from "@/lib/finance";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import AddExpenseDialog from "@/components/AddExpenseDialog";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import useBulkSelection from "@/hooks/useBulkSelection";
import useInlineBulkEdit from "@/hooks/useInlineBulkEdit";
import { EditableText, EditableSelect } from "@/components/EditableCell";
import ExpenseLedgerRow, { STICKY_W, COL_W, PAY_MODES, categoriesFor, subsFor } from "@/components/ExpenseLedgerRow";
import BulkSaveBar from "@/components/BulkSaveBar";
import QuickAddCategoryDialog from "@/components/QuickAddCategoryDialog";
import QuickAddVendorDialog from "@/components/QuickAddVendorDialog";
import { FilterBar, FilterField, FilterDate } from "@/components/FinanceFilterBar";
import FinanceTotalsBar from "@/components/FinanceTotalsBar";
import { matchesMonth } from "@/lib/finance";

const EMPTY_FILTERS = {
  dateFrom: "", dateTo: "", account: "all", party: "", narration: "",
  project: "all", department: "all", category: "all", subCategory: "all",
  amountMin: "", amountMax: "", payMode: "all", month: "all", submittedBy: "all", status: "all",
};

// Phase 94 — every visible column is an inline-editable cell; the pencil
// "Edit dialog" is gone. Edit → row highlights orange → "Bulk Save Changes"
// commits every pending edit in one request.
// Phase 96 — renamed from EntriesLedger → ExpensesLedger ("Entries" tab is
// now "Expenses" everywhere). Backing collection is finance_expenses
// (unchanged — it was already correctly named).
// Phase 122 — `presetFilters` (from the Finance page's Analytics Panel
// Global Filter Bar) is merged straight into this ledger's own Advanced
// Filter Bar whenever it changes, so a chart click or a Global Filter Bar
// dropdown instantly narrows this table too — no separate filter UI needed.
// Oct 2026 — rows are memo'd `ExpenseLedgerRow`s with stable callbacks and
// memoized option lists; text cells commit on blur, so typing in one cell
// no longer re-renders the whole table.
export default function ExpensesLedger({ rows, categories, onChanged, month = "all", presetFilters, quickFilter, onClearFilter }) {
  const [view, setView] = useState("all"); // "all" | "pending"
  const [sources, setSources] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tree, setTree] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  // Feb 2026 — Inline Party Rename: "+ Add new vendor" quick-add dialog.
  // Tracks which row's Party cell triggered it so the newly-created vendor
  // can be auto-selected into that exact row, no re-opening the dropdown.
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [activeVendorRowId, setActiveVendorRowId] = useState(null);
  const [docPreview, setDocPreview] = useState(null); // { url, title }
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (presetFilters) setFilters((prev) => ({ ...prev, ...presetFilters }));
  }, [presetFilters]);

  const submittedByOptions = useMemo(() => {
    const byKey = new Map();
    (rows || []).forEach((r) => {
      const raw = expensePersonName(r) || "Unattributed";
      const key = normalizePersonKey(raw);
      if (!byKey.has(key)) byKey.set(key, raw);
    });
    return [...byKey.values()].sort();
  }, [rows]);


  const reloadTaxonomy = () => api.get("/finance/categories").then(({ data }) => setTree(data?.tree || []))
    .catch((e) => { if (e?.response?.status === 403) toast.error("Access denied — couldn't load Finance categories."); });
  const reloadVendors = () => api.get("/finance/vendor-options").then(({ data }) => setVendors(data || [])).catch(() => {});

  useEffect(() => {
    api.get("/finance/sources").then(({ data }) => setSources(data || [])).catch(() => setSources([]));
    api.get("/daily-tasks/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    // Phase 100 — strict "Party" dropdown, sourced from the Vendor Registry.
    reloadVendors();
    reloadTaxonomy();
    // Feb 2026 — Account Filter Sync: refetch sources the instant a bank/card
    // account is added/edited/removed anywhere else (e.g. Admin > Sources),
    // so the Account filter chip + inline Account cells never go stale.
    const onSourcesChanged = () => api.get("/finance/sources").then(({ data }) => setSources(data || [])).catch(() => {});
    window.addEventListener("finance-sources-changed", onSourcesChanged);
    return () => window.removeEventListener("finance-sources-changed", onSourcesChanged);
  }, []);

  const vendorOptions = useMemo(() => [...new Set((vendors || []).map((v) => v.company || v.name).filter(Boolean))], [vendors]);

  const monthFiltered = useMemo(() => (rows || []).filter((r) => matchesMonth(r, month)), [rows, month]);

  // Phase 97 Batch 2 — Advanced Filtering Engine (Expenses): Date Range,
  // Account, Party, Narration, Project, Department, Category, Sub-category,
  // Amount, Pay Mode, Month.
  const activeFilterCount = useMemo(() => Object.entries(filters).filter(([, v]) => v && v !== "all").length, [filters]);
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const monthOptions = useMemo(() => Array.from(new Set((rows || []).map((r) => r.month).filter(Boolean))).sort().reverse(), [rows]);
  const filteredRows = useMemo(() => {
    return monthFiltered.filter((r) => {
      if (filters.dateFrom && r.date && r.date < filters.dateFrom) return false;
      if (filters.dateTo && r.date && r.date > filters.dateTo) return false;
      if (filters.account !== "all" && r.source_id !== filters.account) return false;
      if (filters.party && !(r.vendor || "").toLowerCase().includes(filters.party.toLowerCase())) return false;
      if (filters.narration && !(r.description || "").toLowerCase().includes(filters.narration.toLowerCase())) return false;
      if (filters.project !== "all" && r.project_id !== filters.project) return false;
      // Feb 2026 — Category Burn drill-down can send "Uncategorized" as the
      // department/category value (its own display fallback for a blank
      // field) — match that the same way the chart bucketed it.
      if (filters.department !== "all" && (r.department || "Uncategorized") !== filters.department) return false;
      if (filters.category !== "all" && (r.category || "Uncategorized") !== filters.category) return false;
      if (filters.subCategory !== "all" && (r.sub_category || "Uncategorized") !== filters.subCategory) return false;
      if (filters.amountMin !== "" && Number(r.amount || 0) < Number(filters.amountMin)) return false;
      if (filters.amountMax !== "" && Number(r.amount || 0) > Number(filters.amountMax)) return false;
      if (filters.payMode !== "all" && r.payment_mode !== filters.payMode) return false;
      if (filters.month !== "all" && r.month !== filters.month) return false;
      if (filters.submittedBy !== "all" && normalizePersonKey(expensePersonName(r) || "Unattributed") !== normalizePersonKey(filters.submittedBy)) return false;
      // Feb 2026 — Status Filter (Paid/Unpaid quick chip).
      if (filters.status === "paid" && !r.paid) return false;
      if (filters.status === "unpaid" && r.paid) return false;
      return true;
    });
  }, [monthFiltered, filters]);

  const { rows: editRows, updateCell, isDirty, dirtyCount, discardAll, bulkSave, saving } = useInlineBulkEdit({
    sourceRows: filteredRows,
    endpoint: "/finance/expenses/bulk",
    numericFields: ["amount", "fx_rate"],
    onSaved: onChanged,
  });

  const sorted = useMemo(() => [...editRows].sort((a, b) => (b.date || "").localeCompare(a.date || "")), [editRows]);
  const sortedIds = useMemo(() => sorted.map((r) => r.id), [sorted]);
  const bulk = useBulkSelection({ collection: "finance_expenses", ids: sortedIds, onDone: onChanged });

  const remove = useCallback(async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/finance/expenses/${id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [onChanged]);
  const onAddVendor = useCallback((rowId) => { setActiveVendorRowId(rowId); setAddVendorOpen(true); }, []);
  const onAddCategory = useCallback(() => setAddCatOpen(true), []);
  const onPreview = useCallback((p) => setDocPreview(p), []);

  // Feb 2026 — "Mark selected as Paid/Unpaid" bulk actions + Payment Date
  // Stamp. Reuses the existing generic PATCH /finance/expenses/bulk
  // endpoint (already used for the inline-edit Bulk Save) instead of
  // adding new backend routes.
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markingUnpaid, setMarkingUnpaid] = useState(false);
  const markSelectedPaid = async () => {
    const ids = Array.from(bulk.selected);
    if (ids.length === 0) return;
    setMarkingPaid(true);
    try {
      const paidAt = new Date().toISOString().slice(0, 10);
      const { data } = await api.patch("/finance/expenses/bulk", { updates: ids.map((id) => ({ id, paid: true, paid_at: paidAt })) });
      toast.success(`Marked ${data.updated || ids.length} expense${ids.length === 1 ? "" : "s"} as Paid`);
      bulk.clear();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarkingPaid(false); }
  };
  // Reverse action — corrects an accidental "Mark as Paid". Clears the
  // payment-date stamp too, since the row is no longer settled.
  const markSelectedUnpaid = async () => {
    const ids = Array.from(bulk.selected);
    if (ids.length === 0) return;
    setMarkingUnpaid(true);
    try {
      const { data } = await api.patch("/finance/expenses/bulk", { updates: ids.map((id) => ({ id, paid: false, paid_at: null })) });
      toast.success(`Marked ${data.updated || ids.length} expense${ids.length === 1 ? "" : "s"} as Unpaid`);
      bulk.clear();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarkingUnpaid(false); }
  };

  // Feb 2026 — "Move selected to Account": bulk-correct a batch of
  // mis-tagged transactions' Account in one shot, same bulk PATCH endpoint.
  const [moveAccountOpen, setMoveAccountOpen] = useState(false);
  const [moveAccountTarget, setMoveAccountTarget] = useState("");
  const [movingAccount, setMovingAccount] = useState(false);
  const openMoveAccount = () => { setMoveAccountTarget(""); setMoveAccountOpen(true); };
  const confirmMoveAccount = async () => {
    const ids = Array.from(bulk.selected);
    if (ids.length === 0 || !moveAccountTarget) { toast.error("Pick an account first"); return; }
    setMovingAccount(true);
    try {
      const { data } = await api.patch("/finance/expenses/bulk", { updates: ids.map((id) => ({ id, source_id: moveAccountTarget })) });
      toast.success(`Moved ${data.updated || ids.length} expense${ids.length === 1 ? "" : "s"} to ${sourceLabel(moveAccountTarget)}`);
      setMoveAccountOpen(false);
      bulk.clear();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMovingAccount(false); }
  };

  const dlTemplate = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/expenses/template`, "_blank");
  const onBulkPick = () => fileRef.current?.click();
  const onBulkFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/finance/expenses/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} expenses${data.errors?.length ? ` · ${data.errors.length} row errors` : ""}`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const sourceLabel = (sid) => sources.find((s) => s.id === sid)?.name || "—";
  const sortedSources = useMemo(() => sortSources(sources), [sources]);
  const sourceOptions = useMemo(() => sortedSources.map((s) => ({ value: s.id, label: s.name })), [sortedSources]);
  const projectOptions = useMemo(() => projects.map((p) => ({ value: p.id, label: p.name })), [projects]);
  const departments = useMemo(() => tree.map((d) => d.department), [tree]);

  return (
    <div data-testid="expenses-ledger">
      <BulkSaveBar count={dirtyCount} saving={saving} onSave={bulkSave} onDiscard={discardAll} testIdPrefix="expenses-ledger" raised={bulk.selected.size > 0} />

      {quickFilter?.tab === "expenses" && (
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className="px-2 py-1 rounded-sm bg-orange-100 text-[#F54800] font-medium inline-flex items-center gap-1">
            {quickFilter.label}
            <button onClick={onClearFilter}><X size={12} /></button>
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-sm p-0.5" data-testid="expenses-ledger-view-toggle">
            <button onClick={() => setView("all")} className={`px-2.5 py-1 text-[11px] rounded-sm ${view === "all" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="expenses-view-all">All expenses</button>
            <button onClick={() => setView("pending")} className={`px-2.5 py-1 text-[11px] rounded-sm ${view === "pending" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="expenses-view-pending">Pending payments</button>
          </div>
          {view === "all" && (
            <div className="text-xs text-slate-500">{sorted.length} of {(rows || []).length}</div>
          )}
        </div>
        {view === "all" && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onBulkFile} data-testid="expenses-bulk-file" />
            <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={dlTemplate} data-testid="expenses-template-btn">
              <Download size={12} className="mr-1" /> Template
            </Button>
            <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={onBulkPick} disabled={uploading} data-testid="expenses-bulk-btn">
              <FileSpreadsheet size={12} className="mr-1" /> {uploading ? "Uploading…" : "Bulk import"}
            </Button>
            <Button className="rounded-sm h-8 text-xs bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={() => setAddOpen(true)} data-testid="expense-add-btn"><Plus size={12} className="mr-1" /> Add expense</Button>
            <AddExpenseDialog open={addOpen} onOpenChange={setAddOpen} onSaved={onChanged} />
          </div>
        )}
      </div>

      {view === "all" && (
        <FilterBar open={filterOpen} onToggle={() => setFilterOpen((o) => !o)} count={activeFilterCount} onClear={clearFilters} testId="expenses-filter-bar">
          <FilterField label="Date range">
            <div className="flex items-center gap-1">
              <FilterDate value={filters.dateFrom} onChange={(v) => setFilters({ ...filters, dateFrom: v })} placeholder="From" testId="expenses-filter-date-from" />
              <FilterDate value={filters.dateTo} onChange={(v) => setFilters({ ...filters, dateTo: v })} placeholder="To" testId="expenses-filter-date-to" />
            </div>
          </FilterField>
          <FilterField label="Account">
            <EditableSelect value={filters.account} onChange={(v) => setFilters({ ...filters, account: v })} options={[{ value: "all", label: "All accounts" }, ...sortedSources.map((s) => ({ value: s.id, label: s.name }))]} testId="expenses-filter-account" />
          </FilterField>
          <FilterField label="Party">
            <EditableText className="w-28 !h-8 !border-slate-200 bg-white" placeholder="Search" value={filters.party} onChange={(v) => setFilters({ ...filters, party: v })} testId="expenses-filter-party" />
          </FilterField>
          <FilterField label="Narration">
            <EditableText className="w-36 !h-8 !border-slate-200 bg-white" placeholder="Search" value={filters.narration} onChange={(v) => setFilters({ ...filters, narration: v })} testId="expenses-filter-narration" />
          </FilterField>
          <FilterField label="Project">
            <EditableSelect value={filters.project} onChange={(v) => setFilters({ ...filters, project: v })} options={[{ value: "all", label: "All projects" }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} testId="expenses-filter-project" />
          </FilterField>
          <FilterField label="Department">
            <EditableSelect value={filters.department} onChange={(v) => setFilters({ ...filters, department: v, category: "all", subCategory: "all" })} options={[{ value: "all", label: "All departments" }, ...departments.map((d) => ({ value: d, label: d }))]} testId="expenses-filter-department" />
          </FilterField>
          <FilterField label="Status">
            <EditableSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{ value: "all", label: "All" }, { value: "paid", label: "Paid" }, { value: "unpaid", label: "Unpaid" }]} testId="expenses-filter-status" />
          </FilterField>
          <FilterField label="Category">
            <EditableSelect value={filters.category} onChange={(v) => setFilters({ ...filters, category: v, subCategory: "all" })} options={[{ value: "all", label: "All categories" }, ...categoriesFor(tree, filters.department === "all" ? undefined : filters.department).map((c) => ({ value: c, label: c }))]} testId="expenses-filter-category" />
          </FilterField>
          <FilterField label="Sub-category">
            <EditableSelect value={filters.subCategory} onChange={(v) => setFilters({ ...filters, subCategory: v })} options={[{ value: "all", label: "All sub-categories" }, ...subsFor(tree, filters.department === "all" ? undefined : filters.department, filters.category === "all" ? undefined : filters.category).map((s) => ({ value: s, label: s }))]} testId="expenses-filter-subcategory" />
          </FilterField>
          <FilterField label="Amount range">
            <div className="flex items-center gap-1">
              <EditableText type="number" className="w-20 !h-8 !border-slate-200 bg-white" placeholder="Min" value={filters.amountMin} onChange={(v) => setFilters({ ...filters, amountMin: v })} testId="expenses-filter-amount-min" />
              <EditableText type="number" className="w-20 !h-8 !border-slate-200 bg-white" placeholder="Max" value={filters.amountMax} onChange={(v) => setFilters({ ...filters, amountMax: v })} testId="expenses-filter-amount-max" />
            </div>
          </FilterField>
          <FilterField label="Pay mode">
            <EditableSelect value={filters.payMode} onChange={(v) => setFilters({ ...filters, payMode: v })} options={[{ value: "all", label: "All" }, ...PAY_MODES.map((p) => ({ value: p, label: p }))]} testId="expenses-filter-pay-mode" />
          </FilterField>
          <FilterField label="Month">
            <EditableSelect value={filters.month} onChange={(v) => setFilters({ ...filters, month: v })} options={[{ value: "all", label: "All months" }, ...monthOptions.map((m) => ({ value: m, label: m }))]} testId="expenses-filter-month" />
          </FilterField>
          <FilterField label="Submitted by">
            <EditableSelect value={filters.submittedBy} onChange={(v) => setFilters({ ...filters, submittedBy: v })} options={[{ value: "all", label: "Everyone" }, ...submittedByOptions.map((p) => ({ value: p, label: p }))]} testId="expenses-filter-submitted-by" />
          </FilterField>
        </FilterBar>
      )}

      {view === "all" && (
        <FinanceTotalsBar rows={sorted} amountField="amount" label="transactions" testId="expenses-totals-bar" />
      )}

      {view === "pending" ? (
        <PendingPayments onPaid={onChanged} />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm table-fixed min-w-[1500px]" data-testid="expenses-ledger-table">
            <thead className="bg-slate-50">
              <tr>
                {bulk.canBulk && (
                  <th className="px-2 py-2 sticky left-0 z-20 bg-slate-50" style={{ width: STICKY_W.checkbox }}><bulk.HeaderCheckbox /></th>
                )}
                <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50" style={{ left: bulk.canBulk ? STICKY_W.checkbox : 0, width: STICKY_W.date }}>Date</th>
                <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date, width: STICKY_W.account }}>Account</th>
                <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date + STICKY_W.account, width: STICKY_W.party }}>Party</th>
                <th className="label-overline px-2 py-2 text-left whitespace-nowrap sticky z-20 bg-slate-50 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]" style={{ left: (bulk.canBulk ? STICKY_W.checkbox : 0) + STICKY_W.date + STICKY_W.account + STICKY_W.party, width: STICKY_W.amount }}>Amount ₹</th>
                {[["Status", COL_W.status], ["Narration", COL_W.narration], ["Project", COL_W.project], ["Department", COL_W.department], ["Category", COL_W.category], ["Sub-category", COL_W.subcategory], ["Pay mode", COL_W.paymode], ["Ccy", COL_W.currency], ["FX rate", COL_W.fx], ["INR Value", COL_W.inrvalue], ["Month", COL_W.month], ["Drive Link", COL_W.drivelink], ["Actions", COL_W.actions]].map(([h, w]) => (
                  <th key={h} className={`label-overline px-2 py-2 ${h === "Actions" ? "text-right" : "text-left"} whitespace-nowrap`} style={{ width: w }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((r) => (
                <ExpenseLedgerRow
                  key={r.id}
                  row={r}
                  dirty={isDirty(r.id)}
                  selected={bulk.isSelected(r.id)}
                  canBulk={bulk.canBulk}
                  RowCheckbox={bulk.RowCheckbox}
                  updateCell={updateCell}
                  sourceOptions={sourceOptions}
                  vendorOptions={vendorOptions}
                  projectOptions={projectOptions}
                  departments={departments}
                  tree={tree}
                  onRemove={remove}
                  onPreview={onPreview}
                  onAddVendor={onAddVendor}
                  onAddCategory={onAddCategory}
                />
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={17} className="px-3 py-6 text-center text-xs text-slate-400">No expenses match — click <b>Add expense</b> above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <bulk.ActionBar>
        <Button
          size="sm"
          onClick={markSelectedPaid}
          disabled={markingPaid}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 rounded-full"
          data-testid="bulk-mark-paid-btn"
        >
          {markingPaid ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}
          Mark selected as Paid
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={markSelectedUnpaid}
          disabled={markingUnpaid}
          className="bg-transparent border-slate-600 hover:bg-slate-800 text-slate-200 h-8 rounded-full"
          data-testid="bulk-mark-unpaid-btn"
        >
          {markingUnpaid ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Ban size={13} className="mr-1.5" />}
          Mark selected as Unpaid
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={openMoveAccount}
          className="bg-transparent border-slate-600 hover:bg-slate-800 text-slate-200 h-8 rounded-full"
          data-testid="bulk-move-account-btn"
        >
          <ArrowRightLeft size={13} className="mr-1.5" /> Move selected to Account
        </Button>
      </bulk.ActionBar>
      <bulk.ConfirmDialog />
      <Dialog open={moveAccountOpen} onOpenChange={(v) => { if (!movingAccount) setMoveAccountOpen(v); }}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="move-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">
              Move {bulk.selected.size} expense{bulk.selected.size === 1 ? "" : "s"} to account
            </DialogTitle>
          </DialogHeader>
          <div className="mt-1">
            <Label className="label-overline">Account</Label>
            <div className="mt-1">
              <EditableSelect value={moveAccountTarget} onChange={setMoveAccountTarget} options={sortedSources.map((s) => ({ value: s.id, label: s.name }))} placeholder="Pick account" testId="move-account-select" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setMoveAccountOpen(false)} disabled={movingAccount} data-testid="move-account-cancel-btn">Cancel</Button>
            <Button onClick={confirmMoveAccount} disabled={movingAccount || !moveAccountTarget} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="move-account-confirm-btn">
              {movingAccount ? "Moving…" : "Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuickAddCategoryDialog open={addCatOpen} onOpenChange={setAddCatOpen} departments={departments} onAdded={reloadTaxonomy} />
      <QuickAddVendorDialog
        open={addVendorOpen}
        onOpenChange={setAddVendorOpen}
        onAdded={(label) => {
          reloadVendors();
          if (activeVendorRowId) updateCell(activeVendorRowId, "vendor", label);
          setActiveVendorRowId(null);
        }}
      />
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </div>
  );
}

/* ───────────── Pending payments (approved vendor/freelancer purchase requests) ─────────────
   Phase 96 — these rows are now real finance_expenses documents (created the
   moment the request is Approved, pre-filled from it), not raw purchase
   requests. Mark paid flips `paid: true` on that same expense row instead of
   inserting a second, duplicate one. */
function PendingPayments({ onPaid }) {
  const { user } = useAuth();
  const can = usePermissions();
  const canEdit = can("finance_edit");
  const canDelete = can("finance_delete");
  const canManage = canEdit || canDelete;
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // { id, reason }
  const [bulkConfirm, setBulkConfirm] = useState(null); // "delete" | "paid" | null

  const load = () => api.get("/finance/pending-payments").then((r) => { setRows(r.data); setSelected(new Set()); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    try { await api.post(`/finance/pending-payments/${id}/mark-paid`, { payment_note: "" }); toast.success("Marked paid"); load(); onPaid?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    setBusy(true);
    try { await api.delete(`/finance/pending-payments/${id}`); toast.success("Payment record deleted"); load(); onPaid?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/finance/pending-payments/${rejectTarget.id}/reject`, { reason: rejectTarget.reason || "" });
      toast.success("Payment rejected");
      setRejectTarget(null);
      load(); onPaid?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const runBulk = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      if (bulkConfirm === "delete") {
        const { data } = await api.post("/finance/pending-payments/bulk-delete", { ids });
        toast.success(`Deleted ${data.deleted || 0} record${data.deleted === 1 ? "" : "s"}`);
      } else {
        const { data } = await api.post("/finance/pending-payments/bulk-mark-paid", { ids });
        toast.success(`Marked ${data.paid || 0} record${data.paid === 1 ? "" : "s"} as paid`);
      }
      setBulkConfirm(null);
      load(); onPaid?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-flat" data-testid="pending-payments-card">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-bold text-sm">Approved purchases awaiting payout (vendors / freelancers)</div>
        <div className="flex items-center gap-2">
          {canManage && selected.size > 0 && (
            <div className="flex items-center gap-1.5" data-testid="pending-pay-bulk-bar">
              <span className="text-[11px] text-slate-500 mr-1">{selected.size} selected</span>
              {canEdit && (
                <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-green-200 text-green-700 hover:bg-green-50" onClick={() => setBulkConfirm("paid")} data-testid="pending-pay-bulk-mark-paid-btn">
                  <Check size={12} className="mr-1" /> Bulk mark as paid
                </Button>
              )}
              {canDelete && (
                <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => setBulkConfirm("delete")} data-testid="pending-pay-bulk-delete-btn">
                  <Trash2 size={12} className="mr-1" /> Bulk delete
                </Button>
              )}
            </div>
          )}
          <span className="text-[11px] px-2 py-0.5 rounded-sm bg-orange-50 text-[#F54800] font-medium">{rows.length} pending</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {canManage && (
                <th className="px-3 py-2 w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all pending payments" data-testid="pending-pay-select-all" />
                </th>
              )}
              {["Item", "Requested by", "Project", "Vendor", "Amount", ""].map((h) => (
                <th key={h} className="label-overline px-3 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className={selected.has(r.id) ? "!bg-orange-50" : ""} data-testid={`pending-pay-${r.id}`}>
                {canManage && (
                  <td className="px-3 py-2">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} aria-label={`Select ${r.description || "row"}`} data-testid={`pending-pay-select-${r.id}`} />
                  </td>
                )}
                <td className="px-3 py-2 text-xs whitespace-pre-wrap max-w-xs">{r.description || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.requested_by_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.project_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.vendor || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono text-right">{fmt(r.amount)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" className="rounded-sm h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => pay(r.id)} data-testid={`mark-paid-${r.id}`}>
                      <Check size={12} className="mr-1" /> Mark paid
                    </Button>
                    {canManage && (
                      <>
                        {canEdit && (
                          <button
                            onClick={() => setRejectTarget({ id: r.id, reason: "" })}
                            className="p-1.5 rounded-sm text-slate-500 hover:text-white hover:bg-amber-600 border border-slate-200 hover:border-amber-600 transition-colors"
                            title="Reject / void"
                            data-testid={`reject-pending-pay-${r.id}`}
                          >
                            <Ban size={13} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => remove(r.id)}
                            disabled={busy}
                            className="p-1.5 rounded-sm text-slate-500 hover:text-white hover:bg-red-600 border border-slate-200 hover:border-red-600 transition-colors disabled:opacity-50"
                            title="Delete permanently"
                            data-testid={`delete-pending-pay-${r.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="px-5 py-6 text-center text-xs text-slate-400">Nothing to pay right now.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!busy && !o) setRejectTarget(null); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reject-pending-pay-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject / void this payment?</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">It will be removed from this payout queue and the Expenses total, and the source Purchase Request will be marked Rejected.</p>
          <div>
            <Label className="label-overline">Reason (optional)</Label>
            <Textarea
              className="rounded-sm mt-1"
              rows={3}
              placeholder="Why is this being rejected?"
              value={rejectTarget?.reason || ""}
              onChange={(e) => setRejectTarget((prev) => ({ ...prev, reason: e.target.value }))}
              data-testid="reject-pending-pay-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectTarget(null)} disabled={busy} data-testid="reject-pending-pay-cancel-btn">Cancel</Button>
            <Button onClick={reject} disabled={busy} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="reject-pending-pay-confirm-btn">
              {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Ban size={13} className="mr-1.5" />} Reject payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => { if (!busy && !o) setBulkConfirm(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="pending-pay-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirm === "delete" ? "Confirm bulk delete" : "Confirm bulk mark as paid"}</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkConfirm === "delete"
                ? `Permanently delete ${selected.size} pending payment record${selected.size === 1 ? "" : "s"}? This cannot be undone.`
                : `Mark ${selected.size} pending payment record${selected.size === 1 ? "" : "s"} as paid?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="pending-pay-bulk-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runBulk(); }}
              disabled={busy}
              className={`rounded-sm text-white ${bulkConfirm === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}
              data-testid="pending-pay-bulk-confirm-btn"
            >
              {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null}
              {bulkConfirm === "delete" ? "Delete" : "Mark as paid"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
